import ParentContainor from '../../components/base/ParentContainor';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { FONTS } from '../../constants/Fonts';
import { getRapFireGameDetail, getTriviaCardV2 } from '../../network/network_callbacks';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import VideoPlayer from '../../components/VideoPlayer';
import OptionSelector from '../../components/rap_fire/OptionSelector';
import GameScreenTopBar from '../../components/GameScreenTopBar';
import RapFireQuestionCount from '../../components/rap_fire/RapFireQuestionCount';
import RapFireQuestionIndexBadge from '../../components/rap_fire/RapFireQuestionIndexBadge';
import {
    HOUSIE_GLASS_CARD_STYLE,
    HOUSIE_SIDE_PANEL_STYLE,
    RAP_FIRE_RIGHT_PANEL_STYLE,
    PAGE_OUTER_PADDING,
} from '../../constants/housieLiveStyles';
import LiveGameVideoStage from '../../components/LiveGameVideoStage';
import LiveGameStatsTicker from '../../components/LiveGameStatsTicker';
import LiveGameTotalMembersPanel from '../../components/LiveGameTotalMembersPanel';
import LiveGameTriviaPanel from '../../components/LiveGameTriviaPanel';
import { logger } from '../../utils/logger';
import mqttService from '../../services/mqttService';
import {
    stashRapFireNextQuestion,
    takeRapFireNextQuestion,
} from '../../utils/rapFirePendingQuestion';
import { resumeBgm } from '../../utils/bgm';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

const RapFireGameLive = () => {
    // Ref to always have latest correctOptionId
    const correctOptionIdRef = useRef();

    const navigate = useNavigate();
    const location = useLocation();
    const { contestId } = location.state;

    const housieVimeoVideoSrcInit = {
        vimeoVideoUrl: null,
        seekTo: 0,
        isPlaying: false,
    };

    const [questionsData, setQuestionsData] = useState(null);
    const questionsDataRef = useRef(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const currentQuestionRef = useRef(null);

    const [joinedUsersListData, setJoinedUsersListData] = useState([]);

    const [questionIndex, setQuestionIndex] = useState({ currentQuestion: 1, totalQuestions: 5 });
    const questionIndexRef = useRef();

    const [housieVimeoVideoSrc, setHousieVimeoVideoSrc] = useState(housieVimeoVideoSrcInit);
    const [entityTriviaCard, setTriviaCardApiData] = useState(null);

    const [isShowCorrectOptions, setIsShowCorrectOptions] = useState(false);
    const [correctAnswerUser, setCorrectAnswerUser] = useState(null);
    const [selectedOptionId, setSelectedOptionId] = useState(null);

    useEffect(() => {
        resumeBgm();
    }, []);

    useEffect(() => {
        questionsDataRef.current = questionsData;
    }, [questionsData]);

    useEffect(() => {
        currentQuestionRef.current = currentQuestion;
    }, [currentQuestion]);

    useEffect(() => {
        if (!contestId) {
            console.error('❌ RapFireGameLive: contestId missing in route state');
            return undefined;
        }

        // Connect MQTT immediately (do not wait for game-detail) so Q1 is not missed
        initMQTTClient();
        getGameDetail();

        return () => {
            disconnectMQTT();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contestId]);

    // Apply any next_question stashed during lobby countdown / before lobbyQuestions loaded
    useEffect(() => {
        if (!questionsData?.lobbyQuestions?.length) return;
        const pending = takeRapFireNextQuestion();
        if (pending && !currentQuestionRef.current) {
            nextQuestionUpdateByPayload(pending);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionsData]);

    useEffect(() => {
        questionIndexRef.current = questionIndex;
    }, [questionIndex]);

    useEffect(() => {
        if (Array.isArray(currentQuestion?.api_question_data?.correctOptionIds) && currentQuestion.api_question_data.correctOptionIds.length > 0) {
            correctOptionIdRef.current = currentQuestion.api_question_data.correctOptionIds[0];
        } else {
            correctOptionIdRef.current = undefined;
        }
    }, [currentQuestion]);

    const getGameDetail = async () => {
        try {
            const gameId = contestId;
            const response = await getRapFireGameDetail(gameId);
            if (response?.data?.data && response?.data?.responseCode === 200) {
                const data = response?.data?.data || {};
                const totalQuestions = data?.totalQuestions || 5;
                setQuestionsData(data);
                setJoinedUsersListData(data?.userState || data?.lobbyUsers || []);
                setQuestionIndex((prev) => ({
                    ...prev,
                    totalQuestions,
                }));
            }
        } catch (error) {
            console.error('❌ Failed to getRapFireGameDetail : ', error);
        }
    }

    const getEntityTriviaCard = async (mqtt_next_question_entity_data) => {
        try {
            const entity = mqtt_next_question_entity_data?.entity;
            const entityId = entity?.[0]?._id || entity?.[0]?.id;
            if (!entityId) return;
            const response = await getTriviaCardV2(entityId);
            setTriviaCardApiData({ entity: response?.data?.data, mqtt_payload: mqtt_next_question_entity_data });
        } catch (error) {
            console.error('❌ Failed to getEntityTriviaCard : ', error);
        }
    }

    const findLobbyQuestion = (questionId, lobbyQuestions) => {
        if (!questionId || !Array.isArray(lobbyQuestions)) return null;
        return lobbyQuestions.find((q) => q?.id === questionId || q?._id === questionId) || null;
    };

    const revealCorrectAnswer = () => {
        setIsShowCorrectOptions(true);
    };

    const nextQuestionUpdateByPayload = (payload) => {
        if (!payload) return;

        const lobbyQuestions = questionsDataRef.current?.lobbyQuestions;
        if (!lobbyQuestions?.length) {
            // Game detail not ready yet — stash and apply when it arrives
            stashRapFireNextQuestion(payload);
            return;
        }

        if (Array.isArray(payload?.usersState) && payload.usersState.length > 0) {
            setJoinedUsersListData(payload.usersState);
        }

        setIsShowCorrectOptions(false);
        setSelectedOptionId(null);
        setCorrectAnswerUser(null);

        const questionId = payload?.questionId;
        const api_question_data = findLobbyQuestion(questionId, lobbyQuestions);

        if (!api_question_data) {
            console.error('❌ next_question: questionId not found in lobbyQuestions', questionId);
            stashRapFireNextQuestion(payload);
            return;
        }

        const questionObj = { mqttData: payload, api_question_data };
        const mqtt_next_question_entity_data = {
            entity: api_question_data?.entity,
            multimedia: api_question_data?.multimedia,
        };
        getEntityTriviaCard(mqtt_next_question_entity_data);
        setCurrentQuestion(questionObj);
        setQuestionIndex((prev) => ({
            currentQuestion: (payload?.questionIndex ?? 0) + 1,
            totalQuestions: payload?.totalQuestions || prev.totalQuestions || 5,
        }));
        setHousieVimeoVideoSrc({
            vimeoVideoUrl: api_question_data?.multimedia?.url || null,
            seekTo: 0,
            isPlaying: true,
        });
    }

    const initMQTTClient = async () => {
        mqttService.connect('game/room/', contestId, {
            'start_game': (payload) => {
                logger.log('MQTT start_game:', payload);
            },
            'end_game': async (payload) => {
                logger.log('MQTT end_game:', payload);
            },
            'countdown': async () => {
                // Skip countdown UI — show game view immediately (tambola-style)
            },
            'next_question': async (payload) => {
                logger.log('MQTT next_question:', payload);
                nextQuestionUpdateByPayload(payload);
            },
            'submit_answer': async (payload) => {
                const isAnswerCorrect = payload?.optionId === correctOptionIdRef.current;
                if (isAnswerCorrect) {
                    revealCorrectAnswer();
                    setCorrectAnswerUser(payload);
                }
            },
            'results': async () => {
                leaderBoard();
            },
            'game_end': async (payload) => {
                logger.log('MQTT game_end:', payload);
            },
            error: (err) => {
                console.error('MQTT error:', err);
            },
            complete: () => {
                logger.log('MQTT subscription complete');
            },
        });
    }

    const disconnectMQTT = () => {
        mqttService.disconnect();
    }

    const leaderBoard = async () => {
        try {
            disconnectMQTT();
            navigate(ROUTE_ENUM.RAP_FIRE_LEADERBOARD.routePath, { replace: true, state: { contestId: contestId } });
        } catch (error) {
            console.error('❌ Failed to leaderBoard : ', error);
        }
    }

    return (
        <>
            <ParentContainor
                route={ROUTE_ENUM.RAP_FIRE_GAME}
                parentContainorStyle={{ justifyContent: 'flex-start' }}
                isShowHeader={false}
                isBreadcrumbVisible={false}
                showCopyRightText={true}
                showBlurredBg={true}
                padding={PAGE_OUTER_PADDING}
            >
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            width: '100%',
                            minHeight: 640,
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '0 0 40px',
                            boxSizing: 'border-box',
                            gap: 0,
                        }}
                    >
                        {/* Figma 17742:25712 — new game nav (no old TopHeader) */}
                        <GameScreenTopBar
                            title={
                                TEXT_LABELS.RAP_FIRE_WELCOME_TITLE_BRAND ||
                                TEXT_LABELS.RAP_FIRE_LOBBY_BRAND_TITLE
                            }
                            onBack={() => navigate(ROUTE_ENUM.SELECT_GAME.routePath)}
                        />

                        {/* Figma 17742: left 372 / center / right 372 */}
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                width: 'calc(100% - 40px)',
                                marginLeft: 20,
                                marginRight: 20,
                                flexDirection: 'row',
                                gap: 24,
                                justifyContent: 'space-between',
                                alignItems: 'stretch',
                                minHeight: 720,
                                position: 'relative',
                                boxSizing: 'border-box',
                            }}
                        >
                                    <div style={HOUSIE_SIDE_PANEL_STYLE}>
                                        <LiveGameTriviaPanel triviaData={entityTriviaCard} />
                                    </div>

                                    <div
                                        style={{
                                            ...HOUSIE_GLASS_CARD_STYLE,
                                            borderRadius: 20,
                                            border: 'none',
                                            boxShadow: 'none',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: '1 1 0',
                                            width: 0,
                                            minWidth: 0,
                                            minHeight: 640,
                                            padding: '20px',
                                            overflow: 'visible',
                                            position: 'relative',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                    <LiveGameVideoStage
                                        overlay={
                                            <RapFireQuestionCount
                                                questionNumber={questionIndex?.currentQuestion || 1}
                                            />
                                        }
                                    >
                                        <VideoPlayer
                                            videoUrl={housieVimeoVideoSrc?.vimeoVideoUrl}
                                            autoPlay
                                            showControls={false}
                                            isPlaying={housieVimeoVideoSrc?.isPlaying}
                                            muted={false}
                                            seekTo={housieVimeoVideoSrc?.seekTo}
                                            preload="auto"
                                        />
                                    </LiveGameVideoStage>

                                        <div
                                            data-node-id="18423:5131"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                gap: 20,
                                                width: '100%',
                                                marginTop: 16,
                                            }}
                                        >
                                            <div
                                                data-node-id="18423:5092"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 17,
                                                    width: '100%',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <RapFireQuestionIndexBadge
                                                    current={questionIndex?.currentQuestion || 1}
                                                    total={questionIndex?.totalQuestions || 5}
                                                />
                                                <p
                                                    data-node-id="18423:5093"
                                                    style={{
                                                        margin: 0,
                                                        flex: 1,
                                                        minWidth: 0,
                                                        fontFamily: FONTS.INTER_BOLD || FONTS.MONTSERRAT_BOLD,
                                                        fontWeight: 700,
                                                        fontSize: 20,
                                                        lineHeight: '32px',
                                                        letterSpacing: 0.8,
                                                        color: '#003',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {currentQuestion?.api_question_data?.questionText || ''}
                                                </p>
                                            </div>

                                        <div
                                            data-node-id="18423:5103"
                                            style={{
                                                position: 'relative',
                                                zIndex: 5,
                                                width: '100%',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            <OptionSelector
                                                options={currentQuestion?.api_question_data?.options || []}
                                                isShowCorrectOption={isShowCorrectOptions}
                                                correctOption={currentQuestion?.api_question_data?.correctOptionIds?.[0] || ''}
                                                correctAnswerUser={correctAnswerUser}
                                                selectedOptionId={selectedOptionId}
                                                onSelectOption={(option) => {
                                                    setSelectedOptionId(option?.id ?? null);
                                                    setCorrectAnswerUser({
                                                        optionId: option?.id,
                                                        ...option,
                                                    });
                                                    const correctOptionId =
                                                        currentQuestion?.api_question_data?.correctOptionIds?.[0];
                                                    const isCorrect =
                                                        option?.isCorrect ||
                                                        (correctOptionId && option?.id === correctOptionId);
                                                    if (isCorrect) {
                                                        revealCorrectAnswer();
                                                    }
                                                }}
                                            />
                                        </div>
                                        </div>

                                    </div>

                                    <div style={RAP_FIRE_RIGHT_PANEL_STYLE}>
                                        <LiveGameTotalMembersPanel
                                            members={joinedUsersListData}
                                            showQuestionProgress
                                        />
                                    </div>
                        </div>

                        {/* Figma 18115:3669 — stats ticker under all 3 columns */}
                        <LiveGameStatsTicker
                            items={[
                                '200+ Played RapFire today',
                                '26.5 M RapCoins Credited to Player',
                                '200 Playing in Corporate Room',
                            ]}
                        />
                    </div>
            </ParentContainor>

        </>
    );
};

export default RapFireGameLive;
